//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LLMContext, type LLMContextValue } from '@/contexts/LLMContext';
import { configService } from '@/services';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useAuth } from '@/hooks/useAuth';
import { useConfig } from '@/hooks/useConfig';

interface LLMProviderProps {
  children: React.ReactNode;
}

// Owns the active-LLM selection.  Like the active dataset, it is not stored as
// independent state: the effective activeLLM is computed from the saved
// preference (userProfile.activeLLM, mirrored locally) + the configured models
// (appConfig.llm_config), so it stays valid automatically and the AI-model
// selector never shows a model that isn't configured.
const LLMProvider = ({ children }: LLMProviderProps) => {
  const handleError = useApiErrorHandler();
  const { appConfig } = useConfig();
  const { userProfile } = useAuth();

  // The user's last explicit LLM preference — a front-end mirror of
  // userProfile.activeLLM, seeded from it below.
  const [llmPreference, setLlmPreference] = useState('');

  // Effective selection: the preference if it is still a configured model,
  // otherwise the first available (or '' if none).
  const activeLLM = useMemo(() => {
    const choices = Object.keys(appConfig?.llm_config ?? {});
    if (llmPreference && choices.includes(llmPreference)) return llmPreference;
    return choices[0] ?? '';
  }, [appConfig, llmPreference]);

  // Seed the preference from the saved profile: login sets it, logout clears it
  // (userProfile goes null).  Validating it against llm_config is the memo's
  // job, not this effect's.
  useEffect(() => {
    setLlmPreference(userProfile?.activeLLM ?? '');
  }, [userProfile?.activeLLM]);

  // Public selection: persist the new preference, then mirror it locally so the
  // computed activeLLM re-derives.  No-op for an unchanged or unconfigured model.
  const setActiveLLM = useCallback(async (llm: string) => {
    const choices = Object.keys(appConfig?.llm_config ?? {});
    if (activeLLM !== llm && (llm === '' || choices.includes(llm))) {
      await handleError(
        'updateUserProfile',
        () => configService.updateUserProfile({ activeLLM: llm }));
      setLlmPreference(llm);
    }
  }, [activeLLM, appConfig, handleError]);

  const value = useMemo((): LLMContextValue => ({
    activeLLM, setActiveLLM,
  }), [activeLLM, setActiveLLM]);

  return (
    <LLMContext.Provider value={value}>
      {children}
    </LLMContext.Provider>
  );
};

export default LLMProvider;
